/**
 * Card and deck helpers. A deck is an ordered, pre-shuffled list of cards;
 * drawing simply takes from the front, so no RNG is needed at draw time (the
 * randomness was spent once, deterministically, when the deck was shuffled at
 * setup). This keeps draws trivially replayable.
 */
import { abilityDef, isAbilityId } from "./abilities";
import { pieceDef } from "./pieces";
import type { AbilityCard, AbilityId, Card, CardTier, PieceCard, PieceTypeId, PlayerId } from "./types";

/** Anything buyable into a deck: a piece type or an ability card. */
export type CardTypeId = PieceTypeId | AbilityId;

/** Deterministic id for a card, unique per player + slot. */
export function cardId(owner: PlayerId, index: number): string {
  return `c${owner}-${index}`;
}

export function makePieceCard(owner: PlayerId, index: number, piece: PieceTypeId): PieceCard {
  return { id: cardId(owner, index), kind: "piece", piece };
}

export function makeAbilityCard(owner: PlayerId, index: number, ability: AbilityId): AbilityCard {
  return { id: cardId(owner, index), kind: "ability", ability };
}

/** The deck-building cost of any card type (piece or ability). */
export function cardTypeCost(id: CardTypeId): number {
  return isAbilityId(id) ? abilityDef(id).cost : pieceDef(id).cost;
}

/** Max copies of a card type allowed in one deck, or undefined for no limit. */
export function cardMaxCopies(id: CardTypeId): number | undefined {
  return isAbilityId(id) ? undefined : pieceDef(id).maxCopies;
}

/**
 * A card's teaching tier (see `CardTier`). Uses the registry's explicit `tier`
 * when set, otherwise derives one from cost so new cards are labelled for free:
 * cheap (<=2) reads as a staple, mid (3-4) a role player, 5+ a bomb.
 */
export function cardTier(id: CardTypeId): CardTier {
  const explicit = isAbilityId(id) ? abilityDef(id).tier : pieceDef(id).tier;
  if (explicit) return explicit;
  const cost = cardTypeCost(id);
  return cost <= 2 ? "staple" : cost <= 4 ? "role" : "bomb";
}

/** The deck-building cost of a concrete card. */
export function cardCost(card: Card): number {
  return card.kind === "ability" ? abilityDef(card.ability).cost : pieceDef(card.piece).cost;
}

/** Total point cost of a list of cards (for budget validation). */
export function deckCost(cards: readonly Card[]): number {
  return cards.reduce((sum, card) => sum + cardCost(card), 0);
}

/**
 * A deck build is a simple map of card type -> count, spanning both pieces and
 * ability cards. The deck builder UI produces one of these; `expandBuild` turns
 * it into concrete cards.
 */
export type DeckBuild = Partial<Record<CardTypeId, number>>;

export function buildCost(build: DeckBuild): number {
  let total = 0;
  for (const [type, count] of Object.entries(build) as [CardTypeId, number][]) {
    total += cardTypeCost(type) * (count ?? 0);
  }
  return total;
}

export function buildSize(build: DeckBuild): number {
  let n = 0;
  for (const count of Object.values(build)) n += count ?? 0;
  return n;
}

/** Materialise a DeckBuild into an ordered list of cards (pre-shuffle). */
export function expandBuild(owner: PlayerId, build: DeckBuild): Card[] {
  const cards: Card[] = [];
  let index = 0;
  for (const [type, count] of Object.entries(build) as [CardTypeId, number][]) {
    for (let i = 0; i < (count ?? 0); i++) {
      cards.push(
        isAbilityId(type)
          ? makeAbilityCard(owner, index++, type)
          : makePieceCard(owner, index++, type),
      );
    }
  }
  return cards;
}
